import { Edge, Node as ReactFlowNode } from '@xyflow/react';
import { appConfig } from '../../helpers/app-config/app-config';
import type { ParseKeys } from 'i18next';
import {
  Stages,
  type GraphData,
  type GraphNode,
  type ProcessMapping,
} from '../../api/process-client/process-client.type';
import { formatCurrencyCompact } from '../../helpers/currency-helpers';
import { PhaseKey } from '../../helpers/phase-labels/phase-labels.type';
import type { ResolvedPhaseLabels } from '../../helpers/phase-labels/phase-labels.type';
import type { StatusLabelResolver } from '../../helpers/status-labels/status-labels.type';
import { baseBlack, secondaryBase } from '../../styles/design-tokens';
import { ViewType } from './process.type';

/**
 * Find a graph node by name from an array of nodes
 *
 * @param {GraphNode[]} nodes - The array of graph nodes to search through
 * @param {string} name - The name of the node to find
 * @returns {GraphNode | undefined} - The found node or undefined
 */
const findNodeByName = (nodes: GraphNode[], name: string): GraphNode | undefined =>
  nodes.find((node) => node.name === name);

/**
 * Find a graph node by order from an array of nodes
 *
 * @param {GraphNode[]} nodes - The array of graph nodes to search through
 * @param {number} order - The order value to match
 * @returns {GraphNode | undefined} - The found node or undefined
 */
const findNodeByOrder = (nodes: GraphNode[], order: number): GraphNode | undefined =>
  nodes.find((node) => node.order === order);

/**
 * Get stage names from the mapping object
 *
 * @param {ProcessMapping} mapping - The process mapping
 * @returns {string[]} - Array of stage names
 */
const getMappedStageNames = (mapping: ProcessMapping): string[] => {
  return Object.keys(mapping.bf_to_customer);
};

/**
 * Get stage names in the correct order from graph data
 *
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @param {boolean} useGroups - Whether to use buckets
 * @returns {string[]} - Array of stage names in the correct order
 */
const getOrderedStageNames = (graphData: GraphData, mapping: ProcessMapping, useGroups: boolean): string[] => {
  // Get all mapped stage names
  const mappedStageNames = getMappedStageNames(mapping);

  // Filter and sort graph nodes by order, then map to names
  let names = graphData.nodes;
  if (useGroups) {
    names = names.filter((node) => mappedStageNames.includes(node.name));
  }
  const orderedNames = names.sort((a, b) => a.order - b.order).map((node) => node.name);

  // If we have ordered names, use them; otherwise fall back to mapping order
  return orderedNames.length > 0 ? orderedNames : mappedStageNames;
};

/**
 * Normalize stage names to Stages enum values
 * Handles various formats: "In Progress", "InProgress", "wip" -> Stages.InProgress
 *
 * @param {string} stage - The stage identifier in any format
 * @returns {string} - Normalized stage matching Stages enum value, or original if not found
 */
const normalizeStageToEnum = (stage: string): string => {
  const normalizedKey = stage.trim().toLowerCase().replace(/\s+/g, '');

  const normalizationMap: Record<string, string> = {
    // Canonical enum-like names
    backlog: Stages.Backlog,
    inprogress: Stages.InProgress,
    inreview: Stages.InReview,
    intest: Stages.InTest,
    deployable: Stages.Deployable,
    done: Stages.Done,
    blocked: Stages.Blocked,

    // Common synonyms / alternate spellings
    wip: Stages.InProgress,
    waiting: Stages.Blocked,
  };

  // Return a known Stages value, or fall back to the original input
  return normalizationMap[normalizedKey] || stage;
};

/**
 * Get the i18n key for a stage
 *
 * @param {string} stage - The stage identifier
 * @returns {string | null} - i18n key for the stage, or null if not found
 */
const getStageI18nKey = (stage: string): ParseKeys<'translation'> | null => {
  // Normalize the stage to enum value first
  const normalizedStage = normalizeStageToEnum(stage);

  // Map enum values to i18n keys. `satisfies Partial<Record<Stages, …>>`
  // restricts keys to members of the Stages enum (catches typos / removed
  // enum values at compile time) while keeping values narrow for the
  // ParseKeys<'translation'> check. Partial allows the function to omit
  // stages we haven't mapped — the `?? null` fallback handles those.
  const stageKeyMap = {
    [Stages.Backlog]: 'common.workflow_steps.backlog',
    [Stages.InProgress]: 'common.workflow_steps.in_progress',
    [Stages.InReview]: 'common.workflow_steps.in_review',
    [Stages.InTest]: 'common.workflow_steps.in_test',
    [Stages.Deployable]: 'common.workflow_steps.deployable',
    [Stages.Done]: 'common.workflow_steps.done',
    [Stages.Blocked]: 'common.workflow_steps.waiting',
  } satisfies Partial<Record<Stages, ParseKeys<'translation'>>>;

  return stageKeyMap[normalizedStage as Stages] ?? null;
};

/**
 * Returns the per-workflow display label override for a stage, if one
 * exists in `resolvedLabels`. Used by the React Flow stage node to short-
 * circuit the i18n cascade when the backend `external_section_map.labels`
 * supplies a non-empty override for the canonical key.
 *
 * @param {string} stage - The stage identifier (any format accepted by
 *   `normalizeStageToEnum`).
 * @param {ResolvedPhaseLabels} [resolvedLabels] - Optional resolved labels
 *   keyed by canonical phase key.
 * @returns {string | null} The override label, or null when no override
 *   applies and the caller should fall back to i18n.
 */
// `Stages` and `PhaseKey` are independent enums whose string values are the
// canonical phase keys. The explicit map enforces compile-time parity so a
// new `Stages` member without a `ResolvedPhaseLabels` key is a TS error.
const STAGE_TO_RESOLVED_LABEL_KEY: Record<Stages, PhaseKey> = {
  [Stages.Backlog]: PhaseKey.Backlog,
  [Stages.Blocked]: PhaseKey.Blocked,
  [Stages.InProgress]: PhaseKey.Wip,
  [Stages.InReview]: PhaseKey.Review,
  [Stages.InTest]: PhaseKey.Test,
  [Stages.Deployable]: PhaseKey.Deployable,
  [Stages.Done]: PhaseKey.Done,
};

const getStageOverrideLabel = (stage: string, resolvedLabels?: ResolvedPhaseLabels): string | null => {
  if (!resolvedLabels) return null;
  const normalizedStage = normalizeStageToEnum(stage);
  if (!(normalizedStage in STAGE_TO_RESOLVED_LABEL_KEY)) {
    return null;
  }
  const override = resolvedLabels[STAGE_TO_RESOLVED_LABEL_KEY[normalizedStage as Stages]];
  if (typeof override === 'string' && override.trim()) {
    return override;
  }
  return null;
};

/**
 * Get the index of a stage in the ordered stages from graph data
 *
 * @param {string} stage - The stage to find
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @returns {number} - The index of the stage
 */
const getStageIndexOrdered = (
  stage: string,
  graphData: GraphData,
  mapping: ProcessMapping,
  useGroups: boolean,
): number => getOrderedStageNames(graphData, mapping, useGroups).indexOf(stage);

/**
 * Calculate the Y position for stages in the graph
 *
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @returns {Record<string, number>} - Object mapping each stage to its Y position
 */
const calculateGridY = (graphData: GraphData, mapping: ProcessMapping, useGroups: boolean): Record<string, number> => {
  const Y_GRID_STEP = useGroups ? 100 : 125;
  const stageNames = getOrderedStageNames(graphData, mapping, useGroups);

  return stageNames.reduce(
    (acc, stage, index) => {
      const prevStage = stageNames[index - 1];
      const prevStageY = acc[prevStage] || 0;
      const base = prevStageY + Y_GRID_STEP;
      const offset =
        (mapping.bf_to_customer[stage]?.length || 0) * 10 + (mapping.bf_to_customer[prevStage]?.length || 0) * 10;

      return {
        ...acc,
        [stage]: base + offset,
      };
    },
    {} as Record<string, number>,
  );
};

/**
 * Calculate the stroke width based on counts
 *
 * @param {number} count - The count value
 * @param {number} totalCount - The total count
 * @returns {number} - The calculated stroke width
 */
const calculateStrokeWidth = (count: number, totalCount: number): number => {
  const MAX_STROKE_WIDTH = parseInt(appConfig.VITE_APP_PROCESS_MAP_MAX_EDGE_STROKE_WIDTH || '20', 10);

  if (totalCount <= 0 || count <= 0) {
    return 0;
  }

  if (count > totalCount) {
    return MAX_STROKE_WIDTH;
  }

  const ratio = count / totalCount;
  return ratio * MAX_STROKE_WIDTH;
};

/**
 * Get display value based on view type
 *
 * @param {number} count - The count value
 * @param {number} total - The total count
 * @param {string} source - Source stage name
 * @param {string} target - Target stage name
 * @param {ViewType} viewType - The view type
 * @returns {number | string} - The formatted value
 */
const getDisplayValue = (
  count: number,
  graphData: GraphData,
  source: string,
  target: string,
  viewType: ViewType,
  currencyCode: string,
): number | string => {
  switch (viewType) {
    case ViewType.Count:
      return count;
    case ViewType.Percentage: {
      if (!graphData?.task_count || !Number.isFinite(graphData.task_count) || !Number.isFinite(count) || count === 0) {
        return '-';
      }
      return `${((count / graphData.task_count) * 100).toFixed(2)}%`;
    }
    case ViewType.CycleTime: {
      const cycleKey = `${source}->${target}`;
      const cycleTime = graphData?.cycle_times?.[cycleKey];
      if (cycleTime !== undefined) {
        return `${cycleTime} days`;
      }
      return `-`;
    }
    case ViewType.Cost: {
      const costKey = `${source}->${target}`;
      const cost = graphData.costs?.[costKey];
      if (cost === undefined) {
        return `${count}`;
      }
      return formatCurrencyCompact(cost, { currency: currencyCode });
    }
    default:
      return `-`;
  }
};

/**
 * Calculate offset X position based on distance
 *
 * @param {number} distance - The distance between nodes
 * @returns {number} - The calculated X offset
 */
const calculateOffsetX = (): number => {
  const X_GRID_STEP = 120;
  return X_GRID_STEP;
};

/**
 * Generate stage nodes for the graph
 *
 * @param {string[]} stageNames - Array of stage names
 * @param {ProcessMapping} mapping - The process mapping
 * @param {Record<string, number>} gridY - Y positions for each stage
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps a raw customer stage
 *   value to its source-cased display label, applied to both the customer-stage
 *   chips and the ungrouped node title; omitted callers get raw values
 * @returns {ReactFlowNode[]} - Array of stage nodes
 */
const generateStageNodes = (
  stageNames: string[],
  mapping: ProcessMapping,
  gridY: Record<string, number>,
  useGroups: boolean,
  isRightGraph: boolean,
  resolveStatusLabelForDisplay?: StatusLabelResolver,
): ReactFlowNode[] => {
  return stageNames.map((stage) => {
    const customerStages = useGroups ? mapping.bf_to_customer[stage] || [] : [stage];
    // Resolve labels here rather than in the node components so the
    // interactive graph and the read-only Flora artifact — which build their
    // nodes through this same function — cannot drift apart.
    const customerStageLabels = resolveStatusLabelForDisplay
      ? Object.fromEntries(
          customerStages.map((customerStage) => [customerStage, resolveStatusLabelForDisplay(customerStage)]),
        )
      : undefined;

    // In grouped mode the title is a canonical stage enum (translated in the
    // component). In ungrouped mode it is `customer_to_bf[stage]` — a canonical
    // section for the interactive graph, or the raw customer status for the
    // Flora artifact's identity mapping.
    let title: string;
    if (useGroups) {
      title = normalizeStageToEnum(stage);
    } else {
      // Read `customer_to_bf` only here: a grouped payload may omit it (it is
      // not used in grouped mode), and an unconditional read would throw.
      const ungroupedTitle = mapping.customer_to_bf?.[stage] || stage;
      // Source-case ONLY raw customer statuses (Flora identity mapping). Leave
      // canonical BF-section titles (interactive graph, e.g. `review`/`test`)
      // untouched so `getStageI18nKey`/`getStageOverrideLabel` can translate
      // them — resolving `review` to a source status `Review` would break that
      // lookup. A canonical section is exactly one that has an i18n key.
      title =
        resolveStatusLabelForDisplay && getStageI18nKey(ungroupedTitle) == null
          ? resolveStatusLabelForDisplay(ungroupedTitle)
          : ungroupedTitle;
    }

    return {
      id: `node-stage-${stage}`,
      type: 'stageNode',
      position: { x: 0, y: gridY[stage] },
      data: {
        // Store the normalized stage enum value, not the translated title
        // Translation will happen in the component to support language changes
        title,
        stage,
        customerStages,
        customerStageLabels,
        isRightGraph,
        // Forward per-workflow `bucket_label_overrides` so the
        // node renders the override label when the backend supplied one
        // on the workflow-scoped `external_section_map` response.
        resolvedLabels: mapping.labels,
      },
    };
  });
};

/**
 * Generate stage edges for the graph
 *
 * @param {string[]} stageNames - Array of stage names
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @param {ViewType} viewType - The view type
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @param {string} currencyCode - The currency code used for cost formatting
 * @returns {Edge[]} - Array of stage edges
 */
const generateStageEdges = (
  stageNames: string[],
  graphData: GraphData,
  mapping: ProcessMapping,
  viewType: ViewType,
  isRightGraph: boolean,
  currencyCode: string,
): Edge[] => {
  const newCOLORS = {
    purple2: secondaryBase,
    black2: baseBlack,
  };

  const edges: Edge[] = [];

  // Create edges between consecutive stages in the ordered list
  for (let i = 0; i < stageNames.length - 1; i++) {
    const sourceStage = stageNames[i];
    const destinationStage = stageNames[i + 1];

    // Get edge data
    const sourceNode = findNodeByName(graphData.nodes, sourceStage);
    const destinationNode = findNodeByName(graphData.nodes, destinationStage);

    let edgeData: { taskCount: number | string; strokeWidth: number } = {
      taskCount: 0,
      strokeWidth: 1,
    };

    if (sourceNode && destinationNode) {
      const count = graphData.edges[sourceNode.order]?.[destinationNode.order] || 0;
      const displayValue = getDisplayValue(
        count,
        graphData,
        sourceNode.name,
        destinationNode.name,
        viewType,
        currencyCode,
      );
      const strokeWidth = calculateStrokeWidth(
        typeof displayValue === 'string' ? parseFloat(displayValue) : displayValue,
        graphData.task_count,
      );

      edgeData = { taskCount: displayValue, strokeWidth: strokeWidth > 1 ? strokeWidth : 1 };
    }

    const workflowMapping = {
      [sourceStage]: mapping.bf_to_customer[sourceStage],
      [destinationStage]: mapping.bf_to_customer[destinationStage],
    };

    // Determine color (special case for 'blocked' stage)
    const color = sourceStage.toLowerCase() === 'blocked' ? newCOLORS.purple2 : newCOLORS.black2;

    edges.push({
      id: `edge-stage-${i}`,
      type: 'stageEdge',
      source: `node-stage-${sourceStage}`,
      target: `node-stage-${destinationStage}`,
      data: {
        value: edgeData.taskCount,
        sourceStage,
        destinationStage,
        mapping: workflowMapping,
        isRightGraph,
      },
      style: { stroke: color, strokeWidth: edgeData.strokeWidth > 1 ? edgeData.strokeWidth : 1 },
    });
  }

  return edges;
};

/**
 * Generate skipped workflow nodes for the graph
 *
 * @param {string} stage - The source stage
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @param {Record<string, number>} gridY - Y positions for each stage
 * @param {ViewType} viewType - The view type
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @returns {ReactFlowNode[]} - Array of skipped workflow nodes
 */
const generateSkippedNodes = (
  stage: string,
  graphData: GraphData,
  mapping: ProcessMapping,
  gridY: Record<string, number>,
  viewType: ViewType,
  useGroups: boolean,
  isRightGraph: boolean,
  currencyCode: string,
): ReactFlowNode[] => {
  const sourceIndex = getStageIndexOrdered(stage, graphData, mapping, useGroups);
  const sourceNode = findNodeByName(graphData.nodes, stage);

  if (!sourceNode) {
    return [];
  }

  const relations = graphData.edges[sourceNode.order] || {};

  const destinations = Object.keys(relations).filter((nodeOrder) => {
    const destination = findNodeByOrder(graphData.nodes, Number(nodeOrder));
    const destinationIndex = destination ? getStageIndexOrdered(destination.name, graphData, mapping, useGroups) : -1;

    if (!destination || destinationIndex < 0) {
      return false;
    }

    return destinationIndex > sourceIndex + 1;
  });

  return destinations
    .map((nodeOrder) => {
      const destination = findNodeByOrder(graphData.nodes, Number(nodeOrder));
      const taskCount = graphData.edges[sourceNode.order]?.[Number(nodeOrder)] || 0;

      if (!destination) {
        return null;
      }

      const value = getDisplayValue(taskCount, graphData, sourceNode.name, destination.name, viewType, currencyCode);

      if (value === 0 || value === '0') {
        return null;
      }

      const destinationStage = destination.name;

      const y = (gridY[destinationStage] - gridY[stage]) / 2 + gridY[stage];
      const destinationIndex = getStageIndexOrdered(destinationStage, graphData, mapping, useGroups);
      const distance = destinationIndex - sourceIndex;
      const x = calculateOffsetX() * distance;

      const workflowMapping = {
        [stage]: mapping.bf_to_customer[stage],
        [destinationStage]: mapping.bf_to_customer[destinationStage],
      };

      return {
        id: `node-skip-${stage}-${destinationStage}`,
        type: 'workflowNode',
        position: { x, y },
        style: { width: '60px', height: '30px' },
        data: {
          value,
          status: 'Skipped',
          source: `node-stage-${stage}`,
          target: `node-stage-${destinationStage}`,
          sourceStage: stage,
          destinationStage,
          mapping: workflowMapping,
          isRightGraph,
        },
      };
    })
    .filter(Boolean) as ReactFlowNode[];
};

/**
 * Generate backwards workflow nodes for the graph
 *
 * @param {string} stage - The source stage
 * @param {GraphData} graphData - The graph data
 * @param {ProcessMapping} mapping - The process mapping
 * @param {Record<string, number>} gridY - Y positions for each stage
 * @param {ViewType} viewType - The view type
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @returns {ReactFlowNode[]} - Array of backwards workflow nodes
 */
const generateBackwardsNodes = (
  stage: string,
  graphData: GraphData,
  mapping: ProcessMapping,
  gridY: Record<string, number>,
  viewType: ViewType,
  useGroups: boolean,
  isRightGraph: boolean,
  currencyCode: string,
): ReactFlowNode[] => {
  const X_GRID_STEP = 120;
  const sourceIndex = getStageIndexOrdered(stage, graphData, mapping, useGroups);
  const sourceNode = findNodeByName(graphData.nodes, stage);

  if (!sourceNode) {
    return [];
  }

  const relations = graphData.edges[sourceNode.order] || {};

  const destinations = Object.keys(relations).filter((nodeOrder) => {
    const destination = findNodeByOrder(graphData.nodes, Number(nodeOrder));

    if (!destination) {
      return false;
    }

    const destinationIndex = getStageIndexOrdered(destination.name, graphData, mapping, useGroups);

    if (destinationIndex < 0) {
      return false;
    }

    return destinationIndex < sourceIndex;
  });

  return destinations
    .map((nodeOrder) => {
      const destination = findNodeByOrder(graphData.nodes, Number(nodeOrder));
      const taskCount = graphData.edges[sourceNode.order]?.[Number(nodeOrder)] || 0;

      if (!destination) {
        return null;
      }

      const value = getDisplayValue(taskCount, graphData, sourceNode.name, destination.name, viewType, currencyCode);

      if (value === 0 || value === '0') {
        return null;
      }

      const destinationStage = destination.name;

      const y = (gridY[stage] - gridY[destinationStage]) / 2 + gridY[destinationStage];
      const destinationIndex = getStageIndexOrdered(destinationStage, graphData, mapping, useGroups);
      const distance = sourceIndex - destinationIndex;

      const offset = distance === 1 ? -X_GRID_STEP + 20 : -calculateOffsetX();
      const x = offset * distance;

      const workflowMapping = {
        [stage]: mapping.bf_to_customer[stage],
        [destinationStage]: mapping.bf_to_customer[destinationStage],
      };

      return {
        id: `node-backwards-${stage}-${destinationStage}`,
        type: 'workflowNode',
        position: { x, y },
        data: {
          value,
          status: 'Backwards',
          source: `node-stage-${stage}`,
          target: `node-stage-${destination.name}`,
          sourceStage: stage,
          destinationStage,
          mapping: workflowMapping,
          isRightGraph,
        },
      };
    })
    .filter(Boolean) as ReactFlowNode[];
};

/**
 * Generate workflow nodes for the graph
 *
 * @param {GraphData} graphData - The graph data
 * @param {string[]} activeStages - Array of active stages
 * @param {ProcessMapping} mapping - The process mapping
 * @param {Record<string, number>} gridY - Y positions for each stage
 * @param {ViewType} viewType - The view type
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @returns {ReactFlowNode[]} - Array of workflow nodes
 */
const generateWorkflowNodes = (
  graphData: GraphData,
  activeStages: string[],
  mapping: ProcessMapping,
  gridY: Record<string, number>,
  viewType: ViewType,
  useGroups: boolean,
  isRightGraph: boolean,
  currencyCode: string,
): ReactFlowNode[] => {
  return activeStages.reduce((resultNodes, stage) => {
    const skipped = generateSkippedNodes(
      stage,
      graphData,
      mapping,
      gridY,
      viewType,
      useGroups,
      isRightGraph,
      currencyCode,
    );
    const backwards = generateBackwardsNodes(
      stage,
      graphData,
      mapping,
      gridY,
      viewType,
      useGroups,
      isRightGraph,
      currencyCode,
    );

    return [...resultNodes, ...skipped, ...backwards];
  }, [] as ReactFlowNode[]);
};

/**
 * Generate workflow edges for the graph
 *
 * @param {ReactFlowNode[]} workflowNodes - Array of workflow nodes
 * @param {GraphData} graphData - The graph data
 * @returns {Edge[]} - Array of workflow edges
 */
const generateWorkflowEdges = (workflowNodes: ReactFlowNode[], graphData: GraphData): Edge[] => {
  const newCOLORS = {
    purple2: '#8569F0',
  };

  return workflowNodes
    .map((node) => {
      const strokeWidth = calculateStrokeWidth(node.data.value as number, graphData.task_count);

      const sourceNodeId = node.data.source as string;
      const targetNodeId = node.data.target as string;

      const incoming = {
        id: `edge-${node.id}-in`,
        type: 'workflowEdgeIn',
        source: sourceNodeId,
        target: node.id,
        sourceHandle: node.data.status === 'Backwards' ? 'left' : 'right',
        targetHandle: node.data.status === 'Backwards' ? 'bottom' : 'top',
        style: { stroke: newCOLORS.purple2, strokeWidth },
        data: {
          status: node.data.status,
        },
      };

      const outgoing = {
        id: `edge-${node.id}-out`,
        type: 'workflowEdgeOut',
        source: node.id,
        target: targetNodeId,
        sourceHandle: node.data.status === 'Backwards' ? 'top' : 'bottom',
        targetHandle: node.data.status === 'Backwards' ? 'left-target' : 'right-target',
        style: { stroke: newCOLORS.purple2, strokeWidth },
        data: {
          status: node.data.status,
        },
      };

      return [incoming, outgoing];
    })
    .flat();
};

/**
 * Main function to calculate graph nodes and edges based on the provided data and mapping.
 * This version doesn't depend on specific stage wording but uses the mapping keys directly.
 *
 * @param {GraphData | undefined} data - The graph data
 * @param {ProcessMapping | null} mapping - The stages mapping for the graph
 * @param {string[]} activeStages - Array of active stages
 * @param {ViewType} viewType - The view type
 * @param {boolean} useGroups - Whether to use buckets
 * @param {boolean} isRightGraph - Whether to generate the right graph
 * @param {string} currencyCode - Currency code used to format cost-view edge labels
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps a raw customer stage
 *   value to its source-cased display label. Omit to render raw values, which
 *   is the correct behavior wherever board scope is unavailable (e.g. the
 *   standalone artifact export route).
 * @returns {{ nodes: ReactFlowNode[]; edges: Edge[] }} - The object containing generated nodes and edges
 * @example
 *   const resolve = createStatusLabelResolver(buildStatusLabelMap(statuses));
 *   const { nodes, edges } = calculateGraph(data, mapping, active, ViewType.Count, false, false, 'USD', resolve);
 */
const calculateGraph = (
  data: GraphData | undefined,
  mapping: ProcessMapping | null,
  activeStages: string[],
  viewType: ViewType,
  useGroups: boolean,
  isRightGraph: boolean,
  currencyCode: string,
  resolveStatusLabelForDisplay?: StatusLabelResolver,
): { nodes: ReactFlowNode[]; edges: Edge[] } => {
  if (!data || !mapping) {
    return {
      nodes: [],
      edges: [],
    };
  }

  // Get stage names in correct order from graph data
  const stageNames = getOrderedStageNames(data, mapping, useGroups);

  // Calculate grid Y positions
  const gridY = calculateGridY(data, mapping, useGroups);

  // Generate stage nodes and edges
  const stageNodes = generateStageNodes(
    stageNames,
    mapping,
    gridY,
    useGroups,
    isRightGraph,
    resolveStatusLabelForDisplay,
  );
  const stageEdges = generateStageEdges(stageNames, data, mapping, viewType, isRightGraph, currencyCode);

  // Generate workflow nodes and edges
  const workflowNodes = generateWorkflowNodes(
    data,
    activeStages,
    mapping,
    gridY,
    viewType,
    useGroups,
    isRightGraph,
    currencyCode,
  );
  const workflowEdges = generateWorkflowEdges(workflowNodes, data);

  return {
    nodes: [...stageNodes, ...workflowNodes],
    edges: [...stageEdges, ...workflowEdges],
  };
};

export { calculateGraph, getStageI18nKey, getStageOverrideLabel };
